import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { OtsAircraft } from "../../Models/OtsAircraft";
import RealtimeHoursOpen from "../OpenEvents/RealTimeHoursOpen";
import "./OtsDashboardList.scss";
import "../../theme/styles.css";
import '../OtsEventCard/OtsDashboardGrid.scss';
import AdvEtrTimeDisplayListView from "./Common/AdvEtrTimeDisplayListView";
import EllipsisTooltip from "../../helpers/EllipsisTooltip";
import EtdTimeDisplayListView from "./Common/EtdTimeDisplayListView";
import BackToTopButton from "../../helpers/BackToTopButton";
import RenderLocAirborneMessagesListView from "./Common/RenderLocAirborneMessagesListView";
import { getSortCode, getSortOrder } from "../../helpers/Util";
import Badge from "../Badge/Badge";
import OtsMoreButtons from "./OtsMoreButtons";
import { IsAuthorized, useAuthorization } from "meportal/AuthorizationProvider";
import { AccessEditButton, AccessTimeline } from "../../constants/PermissionSet";

interface DashboardListProps {
  otsaircraft: OtsAircraft[];
  visibleEventTypes: { [key: string]: boolean };
  toggleVisibility: (eventType: string) => void;
  getEventTypeAccessibleState: (eventName: string) => boolean;
  handleOtsEventClick: (otsEvent: OtsAircraft) => void;
  handleEditOtsEventClick: (otsEvent: OtsAircraft) => void;
  onSortChange: (sort: string) => void;
  onDrag: (eventType: string) => void;
  onDrop: (event: any, eventType: string) => void;
  handleOtsFilterEventClick?: any;
}

const headers = [
  { key: "Aircraft #", label: "Aircraft", sortable: true },
  { key: "Station", label: "Station", sortable: true },
  { key: "ADV", label: "ADV", sortable: true },
  { key: "ETR", label: "ETR", sortable: true },
  { key: "ProblemStatement", label: "Problem statement", sortable: false },
  { key: "ETD", label: "ETD", sortable: false },
  { key: "Hours Open", label: "Time Open", sortable: true },
  { key: "More", label: "", sortable: false }

];

const OtsDashboardList: React.FC<DashboardListProps> = ({
  otsaircraft,
  visibleEventTypes,
  toggleVisibility,
  getEventTypeAccessibleState,
  handleOtsEventClick,
  handleEditOtsEventClick,
  onSortChange,
  onDrag,
  onDrop,
  handleOtsFilterEventClick
}) => {
  const { eventPermissions, userClaims } = useAuthorization();

  const sortField = useSelector((state: any) => state.filterSort.sort);

  const [currentSortField, setCurrentSortField] = useState<string>("");
  const [currentSortOrder, setCurrentSortOrder] = useState<"asc" | "desc" | null>(null);

  //Calculating the sticky header offset for positioning on list dashboard - triggered in .dashboard-list__header - top value.
  useEffect(() => {
    const calculateHeaderOffset = () => {
      const alertBanner = document.querySelector('.alert-banner-container');
      const alertHeight = alertBanner ? alertBanner.getBoundingClientRect().height : 0;
      const totalOffset = alertHeight + 72;

      document.documentElement.style.setProperty(
        '--header-offset',
        `${totalOffset}px`
      );
    };

    calculateHeaderOffset();

    window.addEventListener('resize', calculateHeaderOffset);
    const observer = new MutationObserver(calculateHeaderOffset);
    observer.observe(document.body, { subtree: true, childList: true });

    return () => {
      window.removeEventListener('resize', calculateHeaderOffset);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const sortKey = sortField.split("-")[0];
    const sortOrderCode = sortField.split("-")[1];
    const matchingHeader = headers.find((header) => header.key === sortKey);

    if (matchingHeader) {
      setCurrentSortField(sortKey);
      const sortOrder = getSortOrder(sortKey, sortOrderCode);
      setCurrentSortOrder(sortOrder);
    }
  }, [sortField]);

  //sort change
  const handleHeaderClick = (headerKey: string) => {
    let newSortOrder: "asc" | "desc" = "asc";
    if (currentSortField === headerKey && currentSortOrder === "asc") {
      newSortOrder = "desc";
    }
    setCurrentSortField(headerKey);
    setCurrentSortOrder(newSortOrder);
    const sortCode = getSortCode(headerKey, newSortOrder);
    onSortChange(`${headerKey}-${sortCode}`);
  };

  const dragAndDropEventList = useSelector((state: any) =>
    state.filterSort.dragAndDropEventList
  );

  const groupAircraftByEventType = useMemo(() => {
    return otsaircraft.reduce((acc: { [key: string]: OtsAircraft[] }, aircraft: OtsAircraft) => {
      const eventType = aircraft.EventType;
      acc[eventType] = acc[eventType] || [];
      acc[eventType].push(aircraft);
      return acc;
    }, {});
  }, [otsaircraft]);

  const eventTypes = useMemo(() => {
    const lockedEventTypes: string[] = [];
    const unlockedEventTypes: string[] = [];

    // Separate locked and unlocked event types
    Object.keys(groupAircraftByEventType).forEach((eventType) => {
      if (getEventTypeAccessibleState(eventType)) {
        lockedEventTypes.push(eventType);
      } else {
        unlockedEventTypes.push(eventType);
      }
    });

    lockedEventTypes.sort((a, b) => a.localeCompare(b));

    const savedOrder = dragAndDropEventList.filter((et) => unlockedEventTypes.includes(et));
    const unsavedUnlocked = unlockedEventTypes.filter((et) => !savedOrder.includes(et)).sort((a, b) => a.localeCompare(b));


    const finalUnlockedOrder = [...savedOrder, ...unsavedUnlocked];

    return [...lockedEventTypes, ...finalUnlockedOrder];
  }, [groupAircraftByEventType, getEventTypeAccessibleState, dragAndDropEventList]);


  function handleOtsFilterClick(event: any) {
    handleOtsFilterEventClick(event);
  }

  const handleListRowClick = (aircraft: OtsAircraft) => {
    handleOtsFilterEventClick(null);
    //if user has edit permission then goto edit-ots page
    let okEdit = IsAuthorized(
      aircraft.Airline + ":" + aircraft.EventType,
      AccessEditButton,
      eventPermissions,
      userClaims
    );
    if(okEdit) {
      handleEditOtsEventClick(aircraft);
      return;
    }
    //if user has timeline permission then goto event details page
    let okTimeline = IsAuthorized(
      aircraft.Airline + ":" + aircraft.EventType,
      AccessTimeline,
      eventPermissions,
      userClaims
    );
    if (okTimeline) {
      handleOtsEventClick(aircraft);
      return;
    }
  };

  const permissionAccessCheck = (aircraft: OtsAircraft) => {
    if(IsAuthorized(
      aircraft.Airline + ":" + aircraft.EventType,
      AccessEditButton,
      eventPermissions,
      userClaims
    ) ||
    IsAuthorized(
      aircraft.Airline + ":" + aircraft.EventType,
      AccessTimeline,
      eventPermissions,
      userClaims
    )) {
      return true;
    }
    return false;
  };

  const LockIcon = () => (
    <div className="lock-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M1 7.66663C1 6.25263 1 5.54513 1.4395 5.10613C1.8785 4.66663 2.586 4.66663 4 4.66663H8C9.414 4.66663 10.1215 4.66663 10.5605 5.10613C11 5.54513 11 6.25263 11 7.66663C11 9.08063 11 9.78813 10.5605 10.2271C10.1215 10.6666 9.414 10.6666 8 10.6666H4C2.586 10.6666 1.8785 10.6666 1.4395 10.2271C1 9.78813 1 9.08063 1 7.66663Z" stroke="#73889b" strokeWidth="1.125" />
        <path d="M3 4.66663V3.66663C3 2.87098 3.31607 2.10791 3.87868 1.54531C4.44129 0.982697 5.20435 0.666626 6 0.666626C6.79565 0.666626 7.55871 0.982697 8.12132 1.54531C8.68393 2.10791 9 2.87098 9 3.66663V4.66663" stroke="#73889b" strokeWidth="1.125" strokeLinecap="round" />
      </svg>
    </div>
  );

  const renderEventTypeSection = (eventType: string, aircrafts: OtsAircraft[]) => {
    const isLocked = getEventTypeAccessibleState(eventType);
    const isVisible = visibleEventTypes[eventType] !== false;
    const count = aircrafts.length;

    return (
      <div
        key={eventType}
        className="dashboard-list__section"
        draggable={!isLocked}
        onDragStart={() => !isLocked && onDrag(eventType)}
        onDragOver={(e) => !isLocked && e.preventDefault()}
        onDrop={(event) => !isLocked && onDrop(event,eventType)}
        role="button"
        tabIndex={0}
      >
        <div
          className="eventTypeLabel dashboard-list-event-headers"
          onClick={() => toggleVisibility(eventType)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              toggleVisibility(eventType);
            }
          }}
        >
          <div className="eventTitle">
            {eventType} ({count})
            {isLocked && <LockIcon />}
          </div>
          <div className={`accordion-icon ${isVisible ? 'accordion-icon--open' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
        </div>

        {isVisible && (
          <div className="dashboard-list__items">
            {aircrafts.length > 0 ? (
              aircrafts.map(aircraft => (
                <div
                  key={`${aircraft.AircraftRegistration}-${aircraft.id}`}
                  onClick={() => handleListRowClick(aircraft)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleListRowClick(aircraft); 
                    }
                  }}
                  className={`dashboard-list__item ${permissionAccessCheck(aircraft) ? 'hover-enabled' : ''}`}
                >
                  <div className="dashboard-list__item-col-1">{aircraft.AircraftRegistration}</div>
                  <div className={aircraft?.IsEtopsCapable === "true"
                    ? "dashboard-list__item-col-2 ots-aircraft-section-etops"
                    : "dashboard-list__item-col-2 ots-aircraft-section"}>
                    <div className="vl"></div>
                    <div className="ots-station-list">{aircraft?.Station}</div>
                    <div className="flight-icon list">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="12"
                        viewBox="0 0 20 21"
                        fill="none"
                      >
                        <path
                          d="M20 14.9833V12.9833L11.4883 8.49525L11.4883 3.33325C11.4883 3.33325 11.4883 0.333251 10 0.333251C8.51064 0.333251 8.51064 3.33325 8.51064 3.33325L8.51064 8.49525L0 12.9833L0 14.9833L8.51064 12.4953V16.0823L6.38192 18.3333V20.3333L9.99894 18.7323L13.617 20.3333V18.3333L11.4883 16.0823V12.4953L20 14.9833Z"
                          fill="#00274A"
                        />
                      </svg>
                    </div>
                    <div className="loc-list"><RenderLocAirborneMessagesListView otsEvent={aircraft}/></div>
                  </div>
                  <div className="dashboard-list__item-col-3"><AdvEtrTimeDisplayListView eventTimeUTC={aircraft.AdviseTimeUTC} />  {aircraft.ADVBump > 0 && <Badge data={aircraft.ADVBump} />}</div>
                  <div className="dashboard-list__item-col-4"><AdvEtrTimeDisplayListView eventTimeUTC={aircraft.EstimatedReturnToServiceTimeUTC} /> {aircraft.ETRBump > 0 && <span className="hide-badge-sm"><Badge data={aircraft.ETRBump} /></span>}</div>
                  <EllipsisTooltip title={`${aircraft.ProblemText}`} className="dashboard-list__item-col-5 prob-statement" placement="bottom"><span className="dashboard-list__item-col-5">{aircraft.ProblemText}</span></EllipsisTooltip>
                  <div className="dashboard-list__item-col-6"><EtdTimeDisplayListView eventTime={aircraft.SETD} /></div>
                  <div className="dashboard-list__item-col-7">
                    <RealtimeHoursOpen
                      outOfServiceDateTimeUTC={aircraft?.OutOfServiceDateTimeUTC}
                    />
                  </div>
                  <div className="dashboard-list__item-col-8">
                    <div className="more-padding">
                      <OtsMoreButtons
                        otsEvent={aircraft}
                        handleOtsEventClick={() => handleOtsEventClick(aircraft)}
                        handleOtsFilterClick={handleOtsFilterClick}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="dashboard-list__empty">No items to display</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard-list__wrapper">
      <div className="dashboard-list__header">
        {headers.map((header) => (
          <div
            key={header.key}
            className={`dashboard-list__header-item ${header.sortable ? "sortable" : ""
              } ${currentSortField === header.key ? "sorted" : ""
              }`}
            onClick={() => header.sortable && handleHeaderClick(header.key)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                header.sortable && handleHeaderClick(header.key);
              }
            }}
          >
            {header.label}
          </div>
        ))}
      </div>

      <div className="dashboard-list__container">
        {eventTypes.map((eventType: string) =>
          renderEventTypeSection(eventType, groupAircraftByEventType[eventType])
        )}
        <BackToTopButton />
      </div>
    </div>
  );
};

export default OtsDashboardList;